/**
 * A company's Colyseus rooms, lobby included (SC-422), through Cloud Code. Browsers can't read or
 * write `ColyseusRoom` rows directly, and the cloud functions never return a room's password,
 * only `hasPassword`.
 */
import { parseCloudRun } from '../parseCloud'
import type { ColyseusRoomRecord, TilePoint } from '../../../../types/Maps'
import { createListMemo } from './listMemo'

// One entry per company; any write from this client clears them all (SC-477).
const companyRoomsMemo = createListMemo()

export interface CreateColyseusRoomInput {
  companyId: string
  name: string
  description?: string
  mapId: string
  /** Defaults to the map's start point. */
  spawnPoint?: TilePoint
  password?: string
  maxPlayers?: number | null
  /** Platform admins only: keep the room running when empty. */
  persistent?: boolean
}

/** Only the fields being changed. `password: null` or `''` removes the password. */
export interface ColyseusRoomChanges {
  name?: string
  description?: string
  mapId?: string
  spawnPoint?: TilePoint
  password?: string | null
  maxPlayers?: number | null
  persistent?: boolean
}

export const ColyseusRoomService = {
  /** Memoised for 60 s per company and shared between concurrent callers; `fresh` skips the memo. */
  list(companyId: string, options?: { fresh?: boolean }): Promise<ColyseusRoomRecord[]> {
    return companyRoomsMemo.get(
      companyId,
      () => parseCloudRun<ColyseusRoomRecord[]>('listCompanyRooms', { companyId }),
      options
    )
  },
  /** Platform admins only: every company's rooms. */
  listAll(): Promise<ColyseusRoomRecord[]> {
    return parseCloudRun<ColyseusRoomRecord[]>('listAllColyseusRooms')
  },
  create(input: CreateColyseusRoomInput): Promise<ColyseusRoomRecord> {
    return companyRoomsMemo.afterWrite(parseCloudRun<ColyseusRoomRecord>('createColyseusRoom', { ...input }))
  },
  update(customRoomId: string, changes: ColyseusRoomChanges): Promise<ColyseusRoomRecord> {
    return companyRoomsMemo.afterWrite(parseCloudRun<ColyseusRoomRecord>('updateColyseusRoom', { customRoomId, changes: { ...changes } }))
  },
  reorder(companyId: string, order: string[]): Promise<ColyseusRoomRecord[]> {
    return companyRoomsMemo.afterWrite(parseCloudRun<ColyseusRoomRecord[]>('reorderColyseusRooms', { companyId, order }))
  },
  remove(customRoomId: string): Promise<{ deleted: boolean }> {
    return companyRoomsMemo.afterWrite(parseCloudRun<{ deleted: boolean }>('deleteColyseusRoom', { customRoomId }))
  },
}

export default ColyseusRoomService
